import React from 'react';
import PropTypes from 'prop-types';
import styled, {css, withTheme} from 'styled-components/macro';
import {fill, layoutAttribute} from './cssUtils';
import Popover from './Popover';

const _BodyText = styled.div`
  font-family: ${(props) => props.font};
  font-size: ${(props) => props.fontSize};
  color: ${(props) => props.color || (props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)')};
  text-align: ${(props) => props.alignment ? props.alignment : 'center'};
  opacity: ${(props) => props.opacity ? props.opacity : 1};
  ${props => fill(props.width)};
  ${props => props.maxWidth && `max-width: ${props.maxWidth}`};
  ${props => props.height && `height: ${props.height}`};
  ${props => props.textOverflow && `
    overflow: hidden; 
    text-overflow: ${props.textOverflow};
  `};
  ${layoutAttribute.css};
  margin: ${(props) => props.margin};
  ${props => props.ellipsisOverflow && css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100%);
    margin: 0;
  `}
  ${(props) => props.verticalAlign ? 'display: inline-flex;flex-direction: row;align-items: center;margin-top: 4px;' : ''};
`;

const getPreset = (preset, theme) => {
  const presets = {
    paragraph: {
      margin: '5px',
      font: theme.bodyText.name,
      fontSize: '12px',
      color: theme.dark ? 'white' : 'black',
      opacity: 0.9,
      width: 'fill',
      height: null,
      alignment: 'center',
      textOverflow: null,
    },
    standard: {
      margin: '5px',
      font: theme.bodyText.name,
      fontSize: '12px',
      color: theme.dark ? 'white' : 'black',
      opacity: 0.9,
      width: 'fit-content',
      height: null,
      alignment: 'center',
      textOverflow: null,
    },
    dataLabel: {
      margin: '5px',
      font: theme.bodyText.name,
      fontSize: '10px',
      color: theme.dark ? 'white' : 'black',
      opacity: 0.8,
      width: 'fit-content',
      height: null,
      alignment: 'left',
      textOverflow: null,
    },
    dataLabelLarge: {
      margin: '5px',
      font: theme.bodyText.name,
      fontSize: '14px',
      color: theme.dark ? 'white' : 'black',
      opacity: 0.9,
      width: 'fit-content',
      height: null,
      alignment: 'left',
      textOverflow: null,
    },
    bigNumber: {
      margin: '5px',
      font: theme.bodyText.name,
      fontSize: '20px',
      color: theme.dark ? 'white' : 'black',
      opacity: 1,
      width: 'fit-content',
      height: null,
      alignment: 'left',
      textOverflow: null,
    },
  };
  return presets[preset];
};

/**
 * Descriptive Text component module.
 * @visibleName Descriptive Text
 * @module shared/components/DescriptiveText
 */
const DescriptiveText = (props) => {
  const opts = getPreset(props.preset, props.theme);
  for (const o in opts) {
    if (opts.hasOwnProperty(o) && props.hasOwnProperty(o)) {
      opts[o] = props[o];
    }
  }

  if (props.ellipsisOverflow) {
    return <Popover trigger="hover"
                    display={'block'}
                    margin={opts.margin}
                    width={props.width}
                    maxWidth={props.maxWidth || 'fill'}
                    placement={props.ellipsisOverflow.popoverPlacement}
                    content={props.ellipsisOverflow.popoverContent}>
      <_BodyText {...opts}
                 layout={props.layout}
                 ellipsisOverflow={props.ellipsisOverflow}>
        {props.children}
      </_BodyText>
    </Popover>;
  }

  return <_BodyText {...opts} {...props}
                    layout={props.layout}
                    maxWidth={props.maxWidth}>
    {props.children}
  </_BodyText>;
};

export default withTheme(DescriptiveText);

DescriptiveText.propTypes = {
  /**
   * Preset to use, a group of default values for other inputs. defaults to "paragraph"
   */
  preset: PropTypes.oneOf(['paragraph', 'dataLabel', 'bigNumber', 'dataLabelLarge', 'standard']),
  /**
   * Font-Family should always be based on theme.
   */
  font: PropTypes.string,
  /**
   * CSS Font Size
   */
  fontSize: PropTypes.string,
  /**
   * CSS Opacity
   */
  opacity: PropTypes.number,
  /**
   * CSS margin for wrapper
   */
  margin: PropTypes.string,
  /**
   * The Copy/body of the DescriptiveText
   */
  children: PropTypes.node.isRequired,
  /**
   * The alignment of the DescriptiveText can be left or right, if none is sent in default is center
   */
  alignment: PropTypes.oneOf(['left', 'right', 'center']),
  /**
   * The color of the text. Based on theme if not specified.
   */
  color: PropTypes.string,
  /**
   * Max Width of the Text Input fields, as CSS (e.g. '500px', '50%')
   */
  maxWidth: PropTypes.string,
  /**
   * Width of the Text Input fields, as CSS (e.g. '500px', '50%')
   */
  width: PropTypes.string,
  /**
   * Height of the Text Input fields, as CSS (e.g. '500px', '50%')
   */
  height: PropTypes.string,
  /**
   * Overflow of the Text Input fields, as CSS (e.g. 'auto', 'hidden')
   */
  overflow: PropTypes.string,
  /**
   * Text-overflow of the Text Input fields, as CSS (e.g. 'clip', 'ellipsis')
   * Overflow of the Text Input fields will get set to hidden if textOverflow has a value
   */
  textOverflow: PropTypes.string,
  layout: layoutAttribute.propTypes,
  theme: PropTypes.object,
  /**
   * Cut off text with ellipsis if text is wider than container
   */
  ellipsisOverflow: PropTypes.shape({
    popoverContent: PropTypes.string.isRequired,
    popoverPlacement: PropTypes.oneOf([
      'auto',
      'top',
      'top-start',
      'top-end',
      'right',
      'right-start',
      'right-end',
      'bottom',
      'bottom-start',
      'bottom-end',
      'left',
      'left-start',
      'left-end',
    ]),
  }),
  verticalAlign: PropTypes.bool,
};

DescriptiveText.defaultProps = {
  preset: 'paragraph',
};

